import {
  MinusCircleOutlined,
  PlusCircleOutlined,
  SwapOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { Empty, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import {
  type FieldChange,
  type PropertyChange,
  type SectionChange,
  type StepChange,
  type WorkflowDiff,
} from './versionDiff';

const { Text } = Typography;

type Props = {
  isFr: boolean;
  /** Label of the version being reviewed (e.g. "v3"). */
  nextLabel: string;
  /** Label of the version being compared against (e.g. "v2"). Empty when
   *  `firstSubmission` is true — there's no predecessor to name. */
  prevLabel: string;
  /** True when the version being reviewed is a v1 first submission — every
   *  step is enumerated as "added" and the panel switches its title copy
   *  to reflect that there's no baseline. */
  firstSubmission?: boolean;
  diff: WorkflowDiff;
};

/**
 * Grouped-by-step change summary between two version snapshots. Renders
 * added/removed steps as top-level cards and modified steps as an expandable
 * card with per-section + per-field sub-bullets. Fed a pure {@link
 * WorkflowDiff} from `versionDiff.ts` — no data fetching here.
 *
 * Placement: Approver's `WorkflowReview` slots this above the approve/reject
 * bar for every pending submission. On a v2+ change request it compares
 * against the previous version; on a v1 first submission it enumerates
 * every step as "added" so the approver still gets a consolidated summary
 * of everything they need to review (AC5 on the Preview-and-reject story).
 */
export function VersionDiffPanel({ isFr, nextLabel, prevLabel, firstSubmission, diff }: Props) {
  const { t } = useTranslation();
  const pickTitle = (en: string, fr: string) => (isFr ? fr || en : en || fr || '—');

  return (
    <section
      style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 12,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#111827' }}>
          {firstSubmission
            ? t('approver.diff.titleFirstSubmission', {
                defaultValue: 'First submission — everything in {{next}} is new',
                next: nextLabel,
              })
            : t('approver.diff.title', {
                defaultValue: 'Changes from {{prev}} → {{next}}',
                prev: prevLabel,
                next: nextLabel,
              })}
        </h2>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {firstSubmission
            ? t('approver.diff.subtitleFirstSubmission', {
                defaultValue:
                  'No baseline to compare against — every step, section, and field below is part of this new form.',
              })
            : t('approver.diff.subtitle', {
                defaultValue:
                  'Compared against the previous version so you can see what changed before deciding.',
              })}
        </Text>
      </div>

      {!diff.hasChanges ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('approver.diff.empty', {
            defaultValue: 'No structural changes between these two versions.',
          })}
        />
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 12 }}>
          {diff.steps.map((step) => (
            <StepChangeRow key={`${step.kind}:${step.stepId}`} step={step} pickTitle={pickTitle} />
          ))}
        </ul>
      )}
    </section>
  );
}

// ─── row renderers ──────────────────────────────────────────────────────────

function StepChangeRow({
  step,
  pickTitle,
}: {
  step: StepChange;
  pickTitle: (en: string, fr: string) => string;
}) {
  const { t } = useTranslation();
  const title = pickTitle(step.titleEn, step.titleFr);
  const totalNested =
    step.propertyChanges.length +
    step.sectionChanges.reduce(
      (sum, s) => sum + s.propertyChanges.length + s.fieldChanges.length,
      0,
    );
  return (
    <li
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: 10,
        padding: '14px 16px',
        background: bgForKind(step.kind),
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <ChangeIcon kind={step.kind} />
        <Text strong style={{ fontSize: 14, color: '#111827' }}>
          {t('approver.diff.stepLabel', {
            defaultValue: 'Step — {{title}}',
            title,
          })}
        </Text>
        <KindTag kind={step.kind} />
        {step.kind === 'modified' && totalNested > 0 && (
          <Tag
            style={{
              marginInlineEnd: 0,
              background: '#eff6ff',
              color: '#1d4ed8',
              border: '1px solid #bfdbfe',
            }}
          >
            {t('approver.diff.changeCount', {
              defaultValue: '{{count}} change',
              defaultValue_other: '{{count}} changes',
              count: totalNested,
            })}
          </Tag>
        )}
      </div>

      {(step.propertyChanges.length > 0 || step.sectionChanges.length > 0) && (
        <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
          {step.propertyChanges.length > 0 && <PropertyChangeList changes={step.propertyChanges} />}
          {step.sectionChanges.map((section) => (
            <SectionChangeRow
              key={`${section.kind}:${section.sectionId}`}
              section={section}
              pickTitle={pickTitle}
            />
          ))}
        </div>
      )}
    </li>
  );
}

function SectionChangeRow({
  section,
  pickTitle,
}: {
  section: SectionChange;
  pickTitle: (en: string, fr: string) => string;
}) {
  const { t } = useTranslation();
  const title = pickTitle(section.titleEn, section.titleFr);
  return (
    <div
      style={{
        borderLeft: `3px solid ${accentForKind(section.kind)}`,
        paddingLeft: 12,
        marginLeft: 4,
        display: 'grid',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <ChangeIcon kind={section.kind} />
        <Text style={{ fontSize: 13, color: '#111827' }}>
          {t('approver.diff.sectionLabel', {
            defaultValue: 'Section — {{title}}',
            title,
          })}
        </Text>
        <KindTag kind={section.kind} compact />
      </div>
      {section.propertyChanges.length > 0 && (
        <PropertyChangeList changes={section.propertyChanges} indent />
      )}
      {section.fieldChanges.length > 0 && (
        <ul style={{ margin: 0, paddingInlineStart: 18, display: 'grid', gap: 4 }}>
          {section.fieldChanges.map((field) => (
            <FieldChangeRow
              key={`${field.kind}:${field.fieldId}`}
              field={field}
              pickTitle={pickTitle}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function FieldChangeRow({
  field,
  pickTitle,
}: {
  field: FieldChange;
  pickTitle: (en: string, fr: string) => string;
}) {
  const { t } = useTranslation();
  const label = pickTitle(field.labelEn, field.labelFr);
  return (
    <li style={{ display: 'grid', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <ChangeIcon kind={field.kind} />
        <Text style={{ fontSize: 13 }}>
          {t('approver.diff.fieldLabel', {
            defaultValue: '{{label}} ({{type}})',
            label,
            type: field.type,
          })}
        </Text>
        <KindTag kind={field.kind} compact />
      </div>
      {field.propertyChanges.length > 0 && (
        <PropertyChangeList changes={field.propertyChanges} indent compact />
      )}
    </li>
  );
}

function PropertyChangeList({
  changes,
  indent,
  compact,
}: {
  changes: PropertyChange[];
  indent?: boolean;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <ul
      style={{
        margin: 0,
        paddingInlineStart: indent ? 18 : 0,
        display: 'grid',
        gap: 2,
        listStyle: indent ? 'disc' : 'none',
        color: '#374151',
      }}
    >
      {changes.map((c, i) => (
        <li key={i} style={{ fontSize: compact ? 12 : 13 }}>
          <Text strong style={{ fontSize: compact ? 12 : 13 }}>
            {t(`approver.diff.property.${c.key}`, {
              defaultValue: humanizeKey(c.key),
            })}
            :
          </Text>{' '}
          <span style={{ color: '#6b7280' }}>
            {c.before || t('approver.diff.emptyValue', { defaultValue: '—' })}
          </span>
          <SwapOutlined style={{ margin: '0 6px', color: '#6b7280', fontSize: 11 }} />
          <span style={{ color: '#111827', fontWeight: 500 }}>
            {c.after || t('approver.diff.emptyValue', { defaultValue: '—' })}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ─── small presentational helpers ───────────────────────────────────────────

function ChangeIcon({ kind }: { kind: 'added' | 'removed' | 'modified' }) {
  const color = accentForKind(kind);
  if (kind === 'added') return <PlusCircleOutlined style={{ color }} />;
  if (kind === 'removed') return <MinusCircleOutlined style={{ color }} />;
  return <SyncOutlined style={{ color }} />;
}

function KindTag({ kind, compact }: { kind: 'added' | 'removed' | 'modified'; compact?: boolean }) {
  const { t } = useTranslation();
  const label =
    kind === 'added'
      ? t('approver.diff.kind.added', { defaultValue: 'Added' })
      : kind === 'removed'
        ? t('approver.diff.kind.removed', { defaultValue: 'Removed' })
        : t('approver.diff.kind.modified', { defaultValue: 'Modified' });
  const color = accentForKind(kind);
  return (
    <Tag
      color={kind === 'added' ? 'success' : kind === 'removed' ? 'error' : 'processing'}
      style={{
        marginInlineEnd: 0,
        fontSize: compact ? 11 : 12,
        borderColor: `${color}55`,
      }}
    >
      {label}
    </Tag>
  );
}

function accentForKind(kind: 'added' | 'removed' | 'modified'): string {
  if (kind === 'added') return '#16a34a';
  if (kind === 'removed') return '#dc2626';
  return '#2563eb';
}

function bgForKind(kind: 'added' | 'removed' | 'modified'): string {
  if (kind === 'added') return '#f0fdf4';
  if (kind === 'removed') return '#fef2f2';
  return '#fff';
}

/** Fallback humanization for `PropertyChange.key` when no i18n string exists.
 *  camelCase → "Camel Case". Used behind the i18n `defaultValue`. */
function humanizeKey(key: string): string {
  const spaced = key.replace(/([A-Z])/g, ' $1').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
